import { useState } from "react"
import { observer } from "mobx-react-lite"

import { useScaleApplication } from "@/domains/application/use-scale-application"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useDebouncedCallback } from "@/hooks/use-debounced-callback"

import { AvailableRevenueSourcesView } from "./available-revenue-sources-view"

export const AvailableRevenueSources = observer(() => {
  const scaleApp = useScaleApplication()
  const chainConfig = useChainConfig()

  if (!scaleApp) {
    return null
  }

  const votePageStore = scaleApp.Pages.Vote.Store

  // Local search state for immediate UI update (initialized from store for filter memory)
  const [searchValue, setSearchValue] = useState(votePageStore.availableRevenueSourcesSearch)

  // Debounce search with 200ms delay - update store's debounced search
  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    votePageStore.setAvailableRevenueSourcesSearch(value)
  }, 200)

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    debouncedSetSearch(value)
  }

  // Use MobX computed property - automatically reactive to all observables accessed
  // No useMemo needed - MobX handles memoization and tracks nested observables
  const revenueSources = votePageStore.availableRevenueSources

  const handleUpdateAllocation = (id: string, value: number) => {
    votePageStore.updateSuperVoterPoolAllocation(id, value)
  }

  const totalUsed = votePageStore.superVoterTotalAllocation

  // Get filters from store interfaces
  const aprFilter = votePageStore.availableRevenueSourcesAprFilter
  const tvlFilter = votePageStore.availableRevenueSourcesTvlFilter
  const revenueFilter = votePageStore.availableRevenueSourcesRevenueFilter

  return (
    <AvailableRevenueSourcesView
      aprFilter={aprFilter}
      tvlFilter={tvlFilter}
      revenueFilter={revenueFilter}
      revenueSources={revenueSources}
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
      onUpdateAllocation={handleUpdateAllocation}
      totalUsed={totalUsed}
      listView={votePageStore.listView}
      onListViewChange={(value) => votePageStore.setListViewFromUi(value)}
      governanceToken={chainConfig.tokens.governance}
      availableRevenueSourcesSortBy={votePageStore.availableRevenueSourcesSortBy}
      onAvailableRevenueSourcesSortChange={(sort) =>
        votePageStore.setAvailableRevenueSourcesSortByFromUi(sort)
      }
    />
  )
})
